import React, { useMemo } from 'react'
import { MediaGallery } from '@/common/ui/MediaGallery/MediaGallery'
import { useProductAssets } from '../model'

interface ProductAssetsGalleryProps {
    orgId?: string
    productId?: string
    selectedMediaIndex?: number
    onMediaSelect?: (index: number) => void
    className?: string
    onSelectedImageChange?: (imageUrl: string | null) => void
}

const assetsUrl = __ENDPOINTS_HOST__

export const ProductAssetsGallery: React.FC<ProductAssetsGalleryProps> = ({
    orgId,
    productId,
    selectedMediaIndex = 0,
    onMediaSelect,
    className,
    onSelectedImageChange,
}) => {
    const { data, isLoading, error } = useProductAssets(orgId, productId)

    const getMediaType = (url: string): 'image' | 'video' => {
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi']
        const lowerUrl = url.toLowerCase()
        return videoExtensions.some(ext => lowerUrl.includes(ext)) ? 'video' : 'image'
    }

    const allMedia = useMemo(() => {
        const media: Array<{
            type: 'image' | 'video'
            url: string
            originalIndex: number
        }> = []

        // Добавляем загруженные ассеты
        if (data?.uploaded) {
            const startIndex = media.length
            data.uploaded.forEach((asset, index) => {
                if (asset.url) {
                    media.push({
                        url: asset.url,
                        type: getMediaType(asset.url),
                        originalIndex: startIndex + index,
                    })
                }
            })
        }

        if (data?.generated) {
            const startIndex = media.length
            data.generated.forEach((asset, index) => {
                if (asset.url) {
                    media.push({
                        url: `${assetsUrl}${asset.url}`,
                        type: getMediaType(asset.url),
                        originalIndex: startIndex + index,
                    })
                }
            })
        }

        return media
    }, [data])

    // Уведомляем о выбранном изображении из галереи
    React.useEffect(() => {
        if (onSelectedImageChange && selectedMediaIndex >= 0 && allMedia.length > 0) {
            const selectedMedia = allMedia[selectedMediaIndex]
            const imageUrl = selectedMedia?.type === 'image' ? selectedMedia.url : null
            onSelectedImageChange(imageUrl)
        } else if (onSelectedImageChange && selectedMediaIndex < 0) {
            onSelectedImageChange(null)
        }
    }, [selectedMediaIndex, allMedia, onSelectedImageChange])

    if (!orgId || !productId) {
        return null
    }

    if (isLoading) {
        return (
            <div className='flex items-center justify-center w-full h-[120px]'>
                <p className='text-[#aba7af] text-sm'>Загрузка галереи...</p>
            </div>
        )
    }

    if (error) {
        return (
            <div className='flex items-center justify-center w-full h-[120px]'>
                <p className='text-[#aba7af] text-sm'>Ошибка загрузки галереи</p>
            </div>
        )
    }

    return (
        <MediaGallery
            allMedia={allMedia}
            selectedMediaIndex={selectedMediaIndex}
            onMediaSelect={onMediaSelect || (() => {})}
            title='Галерея'
            className={className}
        />
    )
}
